import React, { useState } from "react";
import { Download } from "lucide-react";
import { safeJsonRequest } from "../../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  SSN_REVEAL: "text-amber-400 bg-amber-900/40",
  SSN_REVEAL_DENIED: "text-red-400 bg-red-900/40",
  LOGIN_SUCCESS: "text-green-400 bg-green-900/40",
  LOGIN_FAILED: "text-red-400 bg-red-900/40",
  LOGIN_BLOCKED: "text-red-400 bg-red-900/40",
  LOGOUT: "text-slate-400 bg-slate-700",
  STATUS_CHANGE: "text-cyan-400 bg-cyan-900/40",
  BULK_STATUS_CHANGE: "text-cyan-400 bg-cyan-900/40",
  NOTE_ADDED: "text-indigo-400 bg-indigo-900/40",
  AUDIT_EXPORT: "text-purple-400 bg-purple-900/40",
};

/**
 * AuditLogPanel — Displays security audit logs with filter, refresh, and CSV export.
 */
const AuditLogPanel = ({ logs, loading, onRefresh, token }) => {
  const [filter, setFilter] = useState("");
  const [exporting, setExporting] = useState(false);

  const filteredLogs = logs.filter((l) => {
    if (!filter) return true;
    return l.action === filter;
  });

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const filterParam = filter ? `?action=${filter}` : "";
      const url = `${API_URL}/api/admin/audit-logs/export${filterParam}`;
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.responseType = "blob";
      xhr.withCredentials = true;

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const contentDisposition = xhr.getResponseHeader("Content-Disposition") || "";
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          const filename = match ? match[1] : `sdora_audit_logs.csv`;

          const blob = new Blob([xhr.response], { type: "text/csv" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
          // Refresh logs to show the AUDIT_EXPORT event
          if (onRefresh) onRefresh();
        }
        setExporting(false);
      };
      xhr.onerror = () => setExporting(false);
      xhr.send();
    } catch {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg text-sm text-white px-3 py-2"
            data-testid="audit-filter"
          >
            <option value="">All Events</option>
            <option value="SSN_REVEAL">SSN Reveals</option>
            <option value="SSN_REVEAL_DENIED">SSN Reveal Denied</option>
            <option value="LOGIN_SUCCESS">Logins</option>
            <option value="LOGIN_FAILED">Failed Logins</option>
            <option value="LOGOUT">Logouts</option>
            <option value="STATUS_CHANGE">Status Changes</option>
            <option value="BULK_STATUS_CHANGE">Bulk Status Changes</option>
            <option value="NOTE_ADDED">Notes Added</option>
            <option value="AUDIT_EXPORT">Audit Exports</option>
          </select>
          <button
            onClick={onRefresh}
            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-300 transition-colors"
            data-testid="refresh-audit-logs"
          >
            Refresh
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{filteredLogs.length} events</span>
          <button
            onClick={handleExportCSV}
            disabled={exporting || filteredLogs.length === 0}
            className="px-3 py-2 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-xs text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="export-audit-csv"
          >
            {exporting ? (
              <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <span className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <p className="text-center py-16 text-sm text-slate-500">No audit events found</p>
      ) : (
        <div className="space-y-1.5">
          {filteredLogs.map((log, idx) => {
            const ts = log.timestamp ? new Date(log.timestamp).toLocaleString("en-US") : "N/A";
            const color = ACTION_COLORS[log.action] || "text-slate-400 bg-slate-700";
            return (
              <div key={idx} className="flex items-center gap-3 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg" data-testid={`audit-log-${idx}`}>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${color}`}>{log.action}</span>
                <span className="text-xs text-slate-300 flex-1 truncate">{log.details}</span>
                <span className="text-xs text-slate-500 whitespace-nowrap">{log.actor}</span>
                <span className="text-xs text-slate-600 whitespace-nowrap">{ts}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
